import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-350334f0"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["aria-label"]
const _hoisted_2 = { class: "viewer-overlays" }
const _hoisted_3 = {
  key: 1,
  class: "part-card-stack"
}

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import AutodeskViewer from '@/components/admin/content/editor/three-d-model/AutodeskViewer.vue'
import type {
  ViewerSelectedModelElement,
  ViewerSelectionPayload
} from '@/components/admin/content/editor/three-d-model/AutodeskViewer.vue'
import ThreeDAssemblyOrderablePartsCard from './ThreeDAssemblyOrderablePartsCard.vue'
import ThreeDPartDetailsCard from './ThreeDPartDetailsCard.vue'
import ThreeDViewerPartSearch from './ThreeDViewerPartSearch.vue'
import {
  useThreeDLockedAssemblyOrdering
} from './useThreeDLockedAssemblyOrdering'
import type {
  ThreeDAssemblyOrderingViewerApi
} from './useThreeDLockedAssemblyOrdering'
import { getMappedThreeDPart } from '@/controllers/library/threeDPartMapping'
import type {
  Part,
  ThreeDViewerPartSearchResult,
  ThreeDViewerPartSearchSource,
  ThreeDViewerPartSearchSourcePayload
} from '@/interfaces/library'
import type { MediaThreeDModelSnapshotDto } from '@/interfaces/admin/threeDModel'

type AutodeskViewerApi = ThreeDAssemblyOrderingViewerApi & {
  clearLibrarySearchFocus: () => void
  setLibraryAssetReady: () => boolean
  setLibrarySearchFocus: (elementIds: number[]) => boolean
}

type ViewerPartSearchApi = {
  clear: () => void
  clearResultSelection: () => void
  setQuery: (value: string) => void
}

const CONSTRAINED_OVERLAY_WIDTH = 1144
type ViewerEventName = 'assetReady' | 'error' | 'viewerShellReady'


export default /*#__PURE__*/_defineComponent({
  __name: 'ThreeDSnapshotViewer',
  props: {
    attemptId: {},
    mobileLayout: { type: Boolean },
    snapshot: {}
  },
  emits: [
  'assetReady',
  'error',
  'partSearchClear',
  'partSearchFocus',
  'selectionChange',
  'viewerShellReady'
],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const store = useStore()
const LibraryAutodeskViewer = AutodeskViewer
const libraryEnableExplode = computed(() => store.getters['user/library3dEnableExplode'])
const libraryShowAdvancedSelectionControls = computed(() =>
  store.getters['user/library3dShowAdvancedSelectionControls']
)
const viewerActiveContent = computed(() =>
  store.state.content.toc?.viewerActiveContent ?? '3d'
)
const autodeskViewer = ref<AutodeskViewerApi | null>(null)
const snapshotViewer = ref<HTMLElement | null>(null)
const partSearch = ref<ViewerPartSearchApi | null>(null)
const hasConstrainedOverlayLayout = ref(false)
const isAssetReady = ref(false)
const partSearchAvailable = ref(false)
const partSearchSource = ref<ThreeDViewerPartSearchSource[]>([])
const snapshotLockedAssemblyParentIds = ref<Record<number, number>>({})
const focusedPartSearchResult = ref<ThreeDViewerPartSearchResult | null>(null)
const focusedPartDetailsSource = ref<ThreeDViewerPartSearchSource | null>(null)
const selectedElement = ref<ViewerSelectedModelElement | null>(null)
const mappedPart = ref<Part | null>(null)
const pricingRequestComplete = ref(false)
const selectionStatus = ref<'error' | 'idle' | 'loading' | 'ready' | 'unavailable'>('idle')
const cardOpen = ref(false)
let mappingRequestId = 0
let pendingPartSearchQuery: string | null = null
let pendingRedirectSelectionDbId: number | null = null
let overlayResizeObserver: ResizeObserver | null = null

const focusedPartElement = computed<ViewerSelectedModelElement | null>(() => {
  const source = focusedPartDetailsSource.value
  if (!source) return null

  return {
    description: source.part.description ?? null,
    id: String(source.elementIds[0] ?? source.part.id ?? source.part.partNumber ?? ''),
    mappedPart: true,
    name: source.part.name ?? source.part.partNumber ?? String(source.part.id ?? ''),
    partNumber: source.part.partNumber ?? null
  }
})
const cardElement = computed(() => selectedElement.value ?? focusedPartElement.value)
const cardPart = computed(() => mappedPart.value)
const cardStatus = computed(() => selectionStatus.value)
const {
  activateMode: activateAssemblyOrderingMode,
  clearMode: clearAssemblyOrderingMode,
  handleDirectSelection: handleAssemblyOrderingDirectSelection,
  orderingModeActive: assemblyOrderingModeActive,
  parts: orderableAssemblyParts,
  reset: resetAssemblyOrdering,
  selectedPartKey: selectedAssemblyChildPartKey,
  selectionRedirects: lockedAssemblySelectionRedirects,
  selectPart: selectAssemblyChildPart,
  visibleAssemblyPartNumber
} = useThreeDLockedAssemblyOrdering({
  cardOpen,
  mappedPart,
  partSearchSource,
  selectedElement,
  snapshotLockedAssemblyParentIds,
  viewer: autodeskViewer,
  viewerActiveContent
})

const props = __props

const mobileLayout = computed(() => props.mobileLayout === true)

const emit = __emit

function emitViewerEvent (event: ViewerEventName) {
  emit(event, {
    attemptId: props.attemptId,
    snapshotId: props.snapshot.id
  })
}

function setAssetReady () {
  if (autodeskViewer.value?.setLibraryAssetReady()) {
    isAssetReady.value = true
    emitViewerEvent('assetReady')
  }
}

function setPartSearchSource ({
  attemptId,
  lockedAssemblyParentIds = {},
  snapshotId,
  source
}: ThreeDViewerPartSearchSourcePayload) {
  if (attemptId !== props.attemptId || snapshotId !== props.snapshot.id) return false

  resetPartSearchContext()
  partSearchSource.value = source
  snapshotLockedAssemblyParentIds.value = lockedAssemblyParentIds
  partSearchAvailable.value = true
  return true
}

function applyPendingPartSearchQuery () {
  if (pendingPartSearchQuery === null || !partSearch.value) return

  partSearch.value.setQuery(pendingPartSearchQuery)
  pendingPartSearchQuery = null
}

function setViewerPartSearchQuery (query: string) {
  pendingPartSearchQuery = query
  applyPendingPartSearchQuery()
}

watch(partSearch, applyPendingPartSearchQuery)

function closePartDetailsCard () {
  clearAssemblyOrderingMode(true)
  cardOpen.value = false
}

function getSearchResultPartDetailsSource (result: ThreeDViewerPartSearchResult) {
  const lockedParentIds: number[] = []
  for (const elementId of result.elementIds) {
    const directParentId = getLockedAssemblyParentId(elementId)
    if (directParentId === null) continue

    const resolvedParentId = resolveLockedAssemblyParentId(directParentId)
    if (resolvedParentId === null) return null
    lockedParentIds.push(resolvedParentId)
  }
  if (lockedParentIds.length === 0) return result

  const uniqueParentIds = [...new Set(lockedParentIds)]
  if (uniqueParentIds.length !== 1) return null

  const parentId = uniqueParentIds[0]
  const parentSource = partSearchSource.value.find(({ elementIds, part }) =>
    part.deleted !== true && elementIds.includes(parentId)
  )
  if (!parentSource) return null

  return {
    ...parentSource,
    elementIds: [parentId, ...parentSource.elementIds.filter(elementId => elementId !== parentId)]
  }
}

function focusPartSearchResult (result: ThreeDViewerPartSearchResult) {
  clearAssemblyOrderingMode()
  const requestId = ++mappingRequestId
  const partDetailsSource = getSearchResultPartDetailsSource(result)
  selectedElement.value = null
  mappedPart.value = null
  pricingRequestComplete.value = false
  focusedPartSearchResult.value = result
  focusedPartDetailsSource.value = partDetailsSource
  selectionStatus.value = partDetailsSource ? 'loading' : 'idle'
  cardOpen.value = !!partDetailsSource
  autodeskViewer.value?.setLibrarySearchFocus(result.elementIds)
  if (partDetailsSource) void loadMappedPart(partDetailsSource.part, requestId)
  emit('partSearchFocus', result)
}

function clearPartSearchFocus () {
  focusedPartSearchResult.value = null
  focusedPartDetailsSource.value = null
  if (!selectedElement.value) {
    mappingRequestId++
    mappedPart.value = null
    pricingRequestComplete.value = false
    selectionStatus.value = 'idle'
    cardOpen.value = false
  }
  autodeskViewer.value?.clearLibrarySearchFocus?.()
  emit('partSearchClear')
}

function clearPartSearchResultSelection () {
  focusedPartSearchResult.value = null
  focusedPartDetailsSource.value = null
  partSearch.value?.clearResultSelection()
  autodeskViewer.value?.clearLibrarySearchFocus?.()
  emit('partSearchClear')
}

function resetPartSearchContext () {
  mappingRequestId++
  resetAssemblyOrdering()
  partSearch.value?.clear()
  autodeskViewer.value?.clearLibrarySearchFocus?.()
  focusedPartSearchResult.value = null
  focusedPartDetailsSource.value = null
  selectedElement.value = null
  mappedPart.value = null
  pricingRequestComplete.value = false
  selectionStatus.value = 'idle'
  cardOpen.value = false
  pendingRedirectSelectionDbId = null
  autodeskViewer.value?.selectElements?.([])
}

function resetSnapshotContext () {
  resetPartSearchContext()
  pendingPartSearchQuery = null
  isAssetReady.value = false
  partSearchAvailable.value = false
  partSearchSource.value = []
  snapshotLockedAssemblyParentIds.value = {}
}

watch(
  [() => props.attemptId, () => props.snapshot.id],
  resetSnapshotContext
)

function getLockedAssemblyParentId (dbId: number) {
  return lockedAssemblySelectionRedirects.value[dbId] ?? null
}

function resolveLockedAssemblyParentId (dbId: number) {
  const visited = new Set<number>()
  let parentId = dbId

  while (!visited.has(parentId)) {
    visited.add(parentId)
    const nextParentId = getLockedAssemblyParentId(parentId)
    if (nextParentId === null) return parentId
    parentId = nextParentId
  }

  return null
}

function getSnapshotMappedPart (element: ViewerSelectedModelElement) {
  const dbId = Number(element.id)
  if (!Number.isInteger(dbId)) return null

  return partSearchSource.value.find(({ elementIds, part }) =>
    part.deleted !== true && elementIds.includes(dbId)
  )?.part ?? null
}

function isFocusedPartSearchElement (dbId: number) {
  const result = focusedPartSearchResult.value
  if (!result) return false

  return result.elementIds.includes(dbId) ||
    focusedPartDetailsSource.value?.elementIds.includes(dbId) === true ||
    Object.values(result.lockedAssemblyParentIds ?? {}).includes(dbId)
}

function handleSelectionEnrichment (payload: ViewerSelectionPayload) {
  const currentElement = selectedElement.value
  const enrichedElement = payload.count === 1 ? payload.element : null
  if (!currentElement || !enrichedElement || currentElement.id !== enrichedElement.id) return

  const shouldMapEnrichedElement =
    !currentElement.partNumber &&
    !!enrichedElement.partNumber &&
    !partSearchAvailable.value &&
    !mappedPart.value &&
    selectionStatus.value !== 'loading'

  selectedElement.value = enrichedElement
  if (shouldMapEnrichedElement) void mapSelectedElement()
}

function handleSelectionChange (payload: ViewerSelectionPayload) {
  emit('selectionChange', payload)
  if (payload.propertyEnrichment) {
    handleSelectionEnrichment(payload)
    return
  }
  const selectedDbId = payload.count === 1 ? payload.selectedDbIds[0] : undefined
  const redirectedDirectSelection =
    !payload.directSelection &&
    pendingRedirectSelectionDbId !== null &&
    selectedDbId === pendingRedirectSelectionDbId
  if (payload.directSelection || redirectedDirectSelection) {
    pendingRedirectSelectionDbId = null
  }
  if (!payload.directSelection && !redirectedDirectSelection) return

  if (payload.directSelection && handleAssemblyOrderingDirectSelection(selectedDbId)) return

  if (payload.directSelection &&
    selectedDbId !== undefined &&
    focusedPartSearchResult.value &&
    !isFocusedPartSearchElement(selectedDbId)) {
    clearPartSearchResultSelection()
  }

  const lockedAssemblyParentId = selectedDbId === undefined
    ? null
    : getLockedAssemblyParentId(selectedDbId)
  if (lockedAssemblyParentId !== null) {
    mappingRequestId++
    selectedElement.value = null
    mappedPart.value = null
    pricingRequestComplete.value = false
    selectionStatus.value = 'idle'
    cardOpen.value = false
    pendingRedirectSelectionDbId = lockedAssemblyParentId
    autodeskViewer.value?.selectElements?.([String(lockedAssemblyParentId)])
    return
  }

  mappingRequestId++
  selectedElement.value = payload.count === 1 ? payload.element : null
  mappedPart.value = null
  pricingRequestComplete.value = false
  cardOpen.value = !!selectedElement.value

  if (!selectedElement.value) {
    selectionStatus.value = 'idle'
    return
  }

  const snapshotMappedPart = getSnapshotMappedPart(selectedElement.value)
  if (snapshotMappedPart) {
    const requestId = ++mappingRequestId
    selectionStatus.value = 'loading'
    void loadMappedPart(snapshotMappedPart, requestId)
    return
  }

  if (partSearchAvailable.value) {
    // The snapshot-scoped source is authoritative, so absence confirms that
    // the selected model element has no mapped Part.
    selectionStatus.value = 'ready'
    return
  }

  mapSelectedElement()
}

function handleSelectionActivated (dbId: number) {
  if (selectedElement.value?.id === String(dbId)) cardOpen.value = true
}

function retryPartDetails () {
  if (selectedElement.value) {
    void mapSelectedElement()
    return
  }

  const partDetailsSource = focusedPartDetailsSource.value
  if (!partDetailsSource) return

  const requestId = ++mappingRequestId
  mappedPart.value = null
  pricingRequestComplete.value = false
  selectionStatus.value = 'loading'
  void loadMappedPart(partDetailsSource.part, requestId)
}

async function mapSelectedElement () {
  const element = selectedElement.value
  const supplierKey = props.snapshot.supplierKey
  if (!element?.partNumber) {
    selectionStatus.value = 'ready'
    return
  }
  // The snapshot supplier belongs to the future Library 3D snapshot contract.
  // Without it, Part Number alone is not sufficient to determine a mapping.
  if (!supplierKey) {
    selectionStatus.value = 'unavailable'
    return
  }

  const requestId = ++mappingRequestId
  selectionStatus.value = 'loading'
  try {
    const part = await getMappedThreeDPart(element.partNumber, supplierKey)
    if (requestId !== mappingRequestId) return
    if (!part) {
      // Library search may omit restricted Parts, so a missing result cannot
      // confirm that the selected element is unmapped.
      selectionStatus.value = 'unavailable'
      return
    }
    await loadMappedPart(part, requestId)
  } catch {
    if (requestId === mappingRequestId) selectionStatus.value = 'error'
  }
}

async function loadMappedPart (part: Part & { visible?: boolean }, requestId: number) {
  if (requestId !== mappingRequestId) return

  mappedPart.value = part
  pricingRequestComplete.value = false
  selectionStatus.value = 'ready'
  if (!part.orderable || part.visible === false) return

  try {
    const pricing = await store.dispatch('content/getPricingWithReturn', {
      ...part,
      mediaIdentifier: store.state.content.toc?.book?.identifier ?? '',
      pricing: part,
      type: 'part'
    })
    if (requestId !== mappingRequestId) return
    mappedPart.value = { ...part, ...pricing }
  } catch {
    // Pricing is optional enrichment; preserve the mapped Part identity.
  } finally {
    // A failed pricing lookup must not permanently gate cart controls.
    if (requestId === mappingRequestId) pricingRequestComplete.value = true
  }
}

onMounted(() => {
  const element = snapshotViewer.value
  if (!element || typeof ResizeObserver === 'undefined') return

  overlayResizeObserver = new ResizeObserver(entries => {
    const width = entries[0]?.contentRect.width ?? 0
    if (width > 0) hasConstrainedOverlayLayout.value = width <= CONSTRAINED_OVERLAY_WIDTH
  })
  overlayResizeObserver.observe(element)
})

onBeforeUnmount(() => {
  overlayResizeObserver?.disconnect()
  mappingRequestId++
  resetAssemblyOrdering()
  autodeskViewer.value?.clearLibrarySearchFocus?.()
  pendingPartSearchQuery = null
  partSearchSource.value = []
  snapshotLockedAssemblyParentIds.value = {}
  focusedPartSearchResult.value = null
  focusedPartDetailsSource.value = null
})

__expose({ setAssetReady, setPartSearchSource, setViewerPartSearchQuery })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", {
    ref_key: "snapshotViewer",
    ref: snapshotViewer,
    class: _normalizeClass(["snapshot-viewer", {
      'has-constrained-overlay-layout': hasConstrainedOverlayLayout.value && !mobileLayout.value,
      'is-mobile-layout': mobileLayout.value
    }]),
    "data-unit": "three-d-snapshot-viewer",
    "aria-label": _ctx.snapshot.snapshotName
  }, [
    _createVNode(_unref(LibraryAutodeskViewer), {
      ref_key: "autodeskViewer",
      ref: autodeskViewer,
      "library-enable-explode": libraryEnableExplode.value,
      "library-mobile-layout": mobileLayout.value,
      "library-mode": "",
      "library-part-number-property": _ctx.snapshot.partNumberProperty,
      "library-selection-redirects": _unref(lockedAssemblySelectionRedirects),
      "library-show-advanced-selection-controls": libraryShowAdvancedSelectionControls.value,
      onError: _cache[0] || (_cache[0] = ($event: any) => (emitViewerEvent('error'))),
      onReady: _cache[1] || (_cache[1] = ($event: any) => (emitViewerEvent('viewerShellReady'))),
      onSelectionActivated: handleSelectionActivated,
      onSelectionChange: handleSelectionChange
    }, null, 8, ["library-enable-explode", "library-mobile-layout", "library-part-number-property", "library-selection-redirects", "library-show-advanced-selection-controls"]),
    _createElementVNode("div", _hoisted_2, [
      (isAssetReady.value && partSearchAvailable.value)
        ? (_openBlock(), _createBlock(ThreeDViewerPartSearch, {
            key: 0,
            ref_key: "partSearch",
            ref: partSearch,
            available: partSearchAvailable.value,
            "snapshot-name": _ctx.snapshot.snapshotName,
            source: partSearchSource.value,
            onClearFocus: clearPartSearchFocus,
            onFocus: focusPartSearchResult
          }, null, 8, ["available", "snapshot-name", "source"]))
        : _createCommentVNode("", true),
      (cardElement.value && cardOpen.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(ThreeDPartDetailsCard, {
              element: cardElement.value,
              part: cardPart.value,
              "pricing-loaded": pricingRequestComplete.value,
              status: cardStatus.value,
              onClose: closePartDetailsCard,
              onRetry: retryPartDetails
            }, null, 8, ["element", "part", "pricing-loaded", "status"]),
            (_unref(orderableAssemblyParts).length > 0)
              ? (_openBlock(), _createBlock(ThreeDAssemblyOrderablePartsCard, {
                  key: 0,
                  "assembly-part-number": _unref(visibleAssemblyPartNumber),
                  items: _unref(orderableAssemblyParts),
                  "ordering-mode-active": _unref(assemblyOrderingModeActive),
                  "selected-part-key": _unref(selectedAssemblyChildPartKey),
                  onSelectPart: _unref(selectAssemblyChildPart),
                  onViewParts: _unref(activateAssemblyOrderingMode)
                }, null, 8, ["assembly-part-number", "items", "ordering-mode-active", "selected-part-key", "onSelectPart", "onViewParts"]))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true)
    ])
  ], 10, _hoisted_1))
}
}

})